import React from 'react';
import { 
  Fab, 
  IconButton, 
  Button, 
  Tooltip,
  Badge 
} from '@mui/material';
import { 
  Chat as ChatIcon,
  ChatBubble as ChatBubbleIcon 
} from '@mui/icons-material';
import { useChatPanel } from '../contexts/ChatPanelContext';

const ChatToggleButton = ({ 
  variant = 'fab', // 'fab', 'icon', 'button'
  position = 'bottom-left', // 'bottom-left', 'bottom-right', 'inline'
  size = 'medium',
  showBadge = false,
  badgeContent = 0,
  ...props 
}) => {
  const { showChatPanel, shouldShowChatPanel } = useChatPanel();

  const handleClick = () => {
    showChatPanel();
  };

  // Don't show toggle if chat panel is already visible (except for inline)
  if (shouldShowChatPanel() && position !== 'inline') {
    return null;
  }

  const getPositionStyles = () => {
    if (position === 'inline') return {};
    
    const baseStyles = {
      position: 'fixed',
      zIndex: 1000,
    };

    switch (position) {
      case 'bottom-left':
        return {
          ...baseStyles,
          bottom: 24,
          left: 24,
        };
      case 'bottom-right':
        return {
          ...baseStyles,
          bottom: 24,
          right: 24,
        };
      default:
        return baseStyles;
    }
  };

  // Tooltip clones its child and passes props to it, so this has to be a single
  // element that can hold a ref — a Fragment would drop them and React warns.
  const withBadge = (icon) => (showBadge ? (
    <Badge badgeContent={badgeContent} color="error">
      {icon}
    </Badge>
  ) : icon);

  let buttonContent;
  if (variant === 'icon') {
    buttonContent = (
      <IconButton
        color="primary"
        size={size}
        onClick={handleClick}
        aria-label="Chat with the AI assistant"
        sx={getPositionStyles()}
        {...props}
      >
        {withBadge(<ChatBubbleIcon />)}
      </IconButton>
    );
  } else if (variant === 'button') {
    buttonContent = (
      <Button
        variant="contained"
        startIcon={<ChatIcon />}
        onClick={handleClick}
        sx={getPositionStyles()}
        {...props}
      >
        Chat with AI
        {showBadge && badgeContent > 0 && (
          <Badge
            badgeContent={badgeContent}
            color="error"
            sx={{ ml: 1 }}
          />
        )}
      </Button>
    );
  } else {
    buttonContent = (
      <Fab
        color="primary"
        size={size}
        onClick={handleClick}
        aria-label="Chat with the AI assistant"
        sx={getPositionStyles()}
        {...props}
      >
        {withBadge(<ChatIcon />)}
      </Fab>
    );
  }

  // Wrap with tooltip for non-inline positions
  if (position !== 'inline') {
    return (
      <Tooltip title="Chat with AI Assistant" placement="top">
        {buttonContent}
      </Tooltip>
    );
  }

  return buttonContent;
};

export default ChatToggleButton;
